import React, { useState, useEffect, useCallback, useContext } from "react";
import styles from "./BusinessProjectPage.module.css";
import Header from "../../components/Header/Header";
import { HeaderType } from "../../components/Header/types/Header";
import Footer from "../../components/Footer/Footer";
import BusinessCard from "../../components/CardsComponents/BusinessCard/BusinessCard";
import { FilterSmallState } from "../../components/FiltersComponents/SmallFiltersComponents/BusinessFilter/BusinessFilter";
import BusinessFilter from "../../components/FiltersComponents/SmallFiltersComponents/BusinessFilter/BusinessFilter";
import Features, { FeaturesData } from "../../components/Features/Features";
import { Helmet } from "react-helmet";
import NotFoundBlock from "../../components/NotFoundBlock/NotFoundBlock";
import Loader from "../../components/Loader/Loader";
import { LoaderContext } from "../../components/Loader/LoaderContext";
import { calculateDiscountedPrice } from "../../utils/discountPriceUtils";
import CardsLoader from "../../assets/images/CardsLoader.gif";
import api from "../../utils/api";

const INITIAL_LOAD_COUNT = 6;
const LOAD_MORE_COUNT = 6;

const getFloorLabel = (floor: number): string => {
  if (floor === 0) return "Цоколь";
  return `${floor} этаж`;
};

export default function BusinessProjectPage() {
  const [cardData, setCardData] = useState<any[]>([]);
  const [displayedCards, setDisplayedCards] = useState<any[]>([]);
  const [featuresData, setFeaturesData] = useState<FeaturesData[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { isInitialLoad, setIsInitialLoad } = useContext(LoaderContext);

  const [smallFilters, setSmallFilters] = useState<FilterSmallState>({
    price_range: [0, 0],
    area_range: [0, 0],
    floor_range: [],
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 720);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featuresResponse, businessResponse] = await Promise.all([
          api.business.getFeatures(),
          api.business.getAll(),
        ]);

        const featuresData = Array.isArray(featuresResponse) ? featuresResponse.map((feature: any) => ({
          id: feature.id,
          text: feature.text,
          image: feature.image,
        })) : [];
        const businesses = Array.isArray(businessResponse) ? businessResponse.filter((business: any) => business.available === true) : [];
        businesses.sort((a: any, b: any) => (b.popular ? 1 : 0) - (a.popular ? 1 : 0));

        const prices = businesses
          .map((business: any) => {
            if (business.price == null) return null;
            const price = typeof business.price === 'string' ? parseFloat(business.price.replace(/ /g, '')) : business.price;
            const discount = (business.discountProperty ?? business.discount_property ?? 0) / 100;
            const pricePerM2 = business.pricePerM2 ?? "0";
            const pricePerM2Str = typeof pricePerM2 === 'number' ? pricePerM2.toString() : (pricePerM2 || "0");

            if (parseFloat(pricePerM2Str.replace(/\s+/g, "")) === 0) {
              return null;
            }

            const discountedPrice = calculateDiscountedPrice(
              pricePerM2 ?? 0,
              Number(discount ?? 0),
              price ?? 0
            );
            return typeof discountedPrice === 'string' ? parseFloat(discountedPrice.replace(/\s+/g, "")) : Number(discountedPrice);
          })
          .filter((price: number | null): price is number => price !== null);

        const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
        const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;

        const formattedPrice = minPrice.toLocaleString("ru-RU");

        if (minPrice > 0) {
          featuresData.push({
            id: featuresData.length + 1,
            text: `От ${formattedPrice} ₽`,
            image: undefined,
          });
        }

        setCardData(businesses);
        setFilteredData(businesses);
        setDisplayedCards(businesses.slice(0, INITIAL_LOAD_COUNT));

        const areas = businesses.map((business: any) =>
          parseFloat((business.area || '').toString().replace(",", "."))
        );
        const minArea = Math.min(...areas);
        const maxArea = Math.max(...areas);

        setSmallFilters((prevFilters) => ({
          ...prevFilters,
          price_range: [minPrice, maxPrice],
          area_range: [minArea, maxArea],
          floor_range: [],
        }));

        setFeaturesData(featuresData);
      } catch (error) {
        console.error("Ошибка при загрузке данных:", error);
      } finally {
        setIsInitialLoad(false);
      }
    };

    fetchData();
  }, [setIsInitialLoad]);

  const loadMoreFilteredCards = useCallback(() => {
    setIsLoadingMore(true);
    setTimeout(() => {
      setDisplayedCards((prevCards) => {
        const nextCards = filteredData.slice(prevCards.length, prevCards.length + LOAD_MORE_COUNT);
        setIsLoadingMore(false);
        return [...prevCards, ...nextCards];
      });
    }, 500);
  }, [filteredData]);

  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    const hasMoreCards = displayedCards.length < filteredData.length;

    const offset = isMobile ? 1400 : 300;

    if (scrollY + windowHeight >= documentHeight - offset && !isLoadingMore && hasMoreCards) {
      loadMoreFilteredCards();
    }
  }, [loadMoreFilteredCards, isLoadingMore, displayedCards.length, filteredData.length, isMobile]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const applySmallFilter = useCallback(
    (filters: FilterSmallState) => {
      setSmallFilters(filters);
      const filtered = cardData.filter((card) => {
        const discountedPrice = calculateDiscountedPrice(
          card.pricePerM2 ?? 0,
          Number(card.discountProperty ?? 0),
          card.price ?? 0
        );
        const price = parseInt(discountedPrice.replace(/\s+/g, ""), 10);
        const area = parseFloat((card.area || '').toString().replace(",", "."));

        const matchesPrice = price >= filters.price_range[0] && price <= filters.price_range[1];
        const matchesArea = area >= filters.area_range[0] && area <= filters.area_range[1];
        
        const matchesFloor = filters.floor_range.length === 0 || filters.floor_range.includes(getFloorLabel(card.floor));

        return matchesPrice && matchesArea && matchesFloor;
      });

      setFilteredData(filtered);
      setDisplayedCards(filtered.slice(0, INITIAL_LOAD_COUNT));
    },
    [cardData]
  );

  return (
    <>
      <Helmet>
        <title>Помещения для бизнеса | Восток Строй</title>
        <meta name="description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="keywords" content="недвижимость, девелопер, Каспийск, новостройки, квартиры" />
        <meta name="author" content="Восток Строй" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://vostokstroy.com/business/status" />

        <meta property="og:title" content="Помещения для бизнеса | Восток Строй" />
        <meta property="og:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta property="og:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />
        <meta property="og:url" content="https://vostokstroy.com/business/status" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Восток Строй" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Помещения для бизнеса | Восток Строй" />
        <meta name="twitter:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="twitter:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />

        <link rel="icon" type="image/svg+xml" href="/favicons/status-fav-32x32.svg" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicons/status-fav-16x16.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicons/status-fav-32x32.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicons/status-apple-touch-icon-180x180.png" />
        <link rel="icon" type="image/png" sizes="180x180" href="/favicons/status-fav-180x180.png" />
      </Helmet>

      {isInitialLoad ? (
        <Loader />
      ) : (
        <>
          <Header theme={HeaderType.Light} />
          <div className={styles.content}>
            <div className={styles.wrapper}>
              <div className={styles.page_title}>
                <h1>
                  Помещения для бизнеса
                  <br />в «Статусе»
                </h1>
              </div>
              <Features features={featuresData} hideIcons={true} />
              <div className={styles.business_content}>
                <BusinessFilter
                  onApplyFilter={applySmallFilter}
                  cardData={cardData}
                  smallFilters={smallFilters}
                  setSmallFilters={setSmallFilters}
                />
                <div className={styles.list_wrapper}>
                  {filteredData.length === 0 ? (
                    <NotFoundBlock type="filter" />
                  ) : (
                    <div className={styles.list}>
                      {displayedCards.map((data, index) => (
                        <div
                          key={data.id}
                          className={styles.business_card}
                          style={{
                            animationDelay: `${index * (isMobile ? 0.02 : 0.03)}s`,
                          }}
                        >
                          <BusinessCard businessData={data} isDiscounted={!!data.discountProperty} />
                        </div>
                      ))}
                      {isLoadingMore && (
                        <div className={styles.loader_container}>
                          <div className={styles.loader}>
                            <img src={CardsLoader} alt="Loading..." />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Footer theme="light"></Footer>
        </>
      )}
    </>
  );
}